import { useQuery } from '@apollo/react-hooks';
import { useAccount } from '@bnnvara/accounts';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { FetchNotificationsHistory } from '../../api/queries/me/notifications.gql';
import withApolloClient from '../../hocs/withApolloClient';
import NotificationsContext from './context';
import getUniqueNotifications from './getUniqueNotifications';
import useNotificationsWebsocket from './useNotificationsWebsocket';

/**
 * The data retrieved in the NotificationsProvider comes from:
 * 1.) BFF to retrieve the last notifications of a user. This is executed only once.
 * 2.) AWS websocket to constantly check for an individual notification.
 * The notifications of both sources are included in one state: notificationsHistory
 */
const NotificationsProvider = withApolloClient(({ children }) => {
  const [notificationsHistory, setNotificationsHistory] = useState([]);
  const [
    amountOfUnreadNotifications,
    setAmountOfUnreadNotifications,
  ] = useState(0);
  const { websocketStatus, latestMessage } = useNotificationsWebsocket({
    setNotificationsHistory,
  });

  const { profile } = useAccount();
  const UID = profile && profile.UID;

  const { data, loading: isLoadingNotificationsHistory } = useQuery(
    FetchNotificationsHistory,
    {
      variables: {
        UID,
        skip: !UID,
      },
    }
  );

  /**
   * Calculate the amount of unread notifications.
   */
  useEffect(() => {
    const numberOfUnreadMessages = notificationsHistory
      .map((notification) => notification.isRead)
      .filter((notification) => notification === false).length;

    setAmountOfUnreadNotifications(numberOfUnreadMessages);
  }, [notificationsHistory]);

  /**
   * It is possible that the websocket data is retrieved before the notificationHistory query.
   * To prevent this data from being lost,
   * the prev state is spread with the new retrieved notifications.
   */
  useEffect(() => {
    if (data && data.me && data.me.notifications) {
      setNotificationsHistory((prevState) =>
        getUniqueNotifications(data.me.notifications, prevState)
      );
    }
  }, [data]);

  return (
    <NotificationsContext.Provider
      value={{
        isLoadingNotificationsHistory,
        notificationsHistory,
        setNotificationsHistory,
        latestMessage,
        websocketStatus,
        amountOfUnreadNotifications,

        getUniqueNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
});
NotificationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
export default NotificationsProvider;
